/**
 * Tiny translation runtime for the widget.
 *
 * Why hand-rolled and not `@formatjs/intl`: bundle size. The widget
 * uses ~16 strings; pulling in a full i18n lib for that is wrong.
 *
 * Adding a locale is two steps:
 *   1. Add a key to `SupportedLocale` in `types.ts`.
 *   2. Add the translation block here.
 *
 * Resolution order: explicit `data-locale` attr -> tenant default
 * (from `/widget/v1/config`) -> `navigator.language` -> "en".
 */

import type { SupportedLocale } from "./types";

type Strings = {
  launcher_aria: string;
  panel_title_default: string;
  opener_default: string;
  composer_placeholder: string;
  send: string;
  sending: string;
  reset: string;
  ai_disclaimer: string;
  error_generic: string;
  error_offline: string;
  // Tool indicators. {name} is interpolated.
  tool_running: string;
  tool_failed: string;
  /** Source-consulted chip rendered under an assistant bubble. */
  source_consulted: string;
  /** Aria label for the textarea send-on-enter hint. */
  composer_input_aria: string;
  // Voice modal microcopy. CLAUDE.md sectie 11: sentence case,
  // no exclamation marks, no em-dashes. The voice modal is the
  // operator's first taste of the ElevenLabs integration; the copy
  // stays calm even when something fails.
  voice_button_aria: string;
  voice_modal_title: string;
  voice_idle_body: string;
  voice_start_label: string;
  voice_connecting: string;
  voice_listening: string;
  voice_live: string;
  voice_end: string;
  voice_close_aria: string;
  voice_back_to_text: string;
  voice_permission_denied: string;
  voice_error_generic: string;
  // Attachment upload microcopy. PR #22 / Slice 2: customer can pick
  // a photo (e.g. of a damaged product) which the widget uploads to
  // `/widget/v1/uploads` before the next chat turn references it by
  // id. Brand voice: calm, declarative, no exclamation marks.
  attach_label: string;
  attach_aria: string;
  attach_uploading: string;
  attach_retry: string;
  attach_remove: string;
  attach_error_too_large: string;
  attach_error_unsupported_type: string;
  attach_error_generic: string;
  // Welcome-state microcopy. The redesigned widget opens on a calm
  // welcome card before the visitor enters chat. Strings live here
  // so future locale switches do not need a parallel translation
  // pass on widget copy.
  welcome_card_title: string;
  welcome_powered_by: string;
  /** Title above the chat-card CTA, e.g. "Chat met {name}". */
  welcome_chat_card_title: string;
  welcome_chat_card_intro: string;
  welcome_chat_card_cta: string;
  welcome_direct_contact_heading: string;
  welcome_channel_whatsapp_label: string;
  welcome_channel_email_label: string;
  welcome_channel_phone_label: string;
  welcome_channel_unavailable: string;
  welcome_back_aria: string;
  // Chat-state header subtitle ("AI AGENT" tag below the agent name).
  chat_header_agent_tag: string;
  /**
   * Small label rendered above an operator_email bubble so the
   * visitor reads it as "a teammate wrote this" rather than another
   * AI turn. `{brand}` is interpolated with the tenant brand_name.
   */
  operator_email_label: string;
  // No-source email-capture form. Rendered as a follow-up bubble
  // when the LAST assistant message in the conversation carries the
  // no_source flag (the canned "ik laat een collega er even naar
  // kijken" escalation). CLAUDE.md sectie 3: the canned reply
  // promises a colleague will return; for anonymous visitors we
  // need a return channel to make good on that promise.
  no_source_email_prompt_title: string;
  no_source_email_prompt_body: string;
  no_source_email_input_placeholder: string;
  no_source_email_send_label: string;
  no_source_email_invalid_error: string;
  no_source_email_network_error: string;
  /** Confirmation line shown after a successful capture. `{email}` is interpolated. */
  no_source_email_thanks: string;
};

// Brand voice rules (CLAUDE.md sectie 11): no exclamation marks, no
// em-dashes, sentence case. The opener_default and disclaimer carry
// that voice into every fallback render the moment the tenant has
// not configured a per-locale opener yet.
const NL: Strings = {
  launcher_aria: "Open chat",
  panel_title_default: "Hulp nodig",
  opener_default: "Hoi, stel je vraag, ik help je graag.",
  composer_placeholder: "Schrijf een bericht",
  send: "Verstuur",
  sending: "Bezig",
  reset: "Nieuw gesprek",
  ai_disclaimer: "Je praat met onze AI-assistent. Bij twijfel schakelen we een collega in.",
  error_generic: "Er ging iets mis. Probeer het zo nog eens.",
  error_offline: "Geen verbinding. Probeer het later nog eens.",
  tool_running: "Even kijken ({name})",
  tool_failed: "{name} liep tegen iets aan",
  source_consulted: "Bron geraadpleegd",
  composer_input_aria: "Schrijf een bericht, druk op Enter om te versturen",
  voice_button_aria: "Praat met Yara",
  voice_modal_title: "Bel met Yara",
  voice_idle_body: "Tik op de knop en stel je vraag, Yara hoort je en antwoordt meteen.",
  voice_start_label: "Tik om te bellen",
  voice_connecting: "Verbinden",
  voice_listening: "Yara luistert",
  voice_live: "Je gesprek loopt",
  voice_end: "Beeindig gesprek",
  voice_close_aria: "Sluit",
  voice_back_to_text: "Terug naar chat",
  voice_permission_denied:
    "Je microfoon is geblokkeerd. Sta het toe in de browser om met Yara te praten.",
  voice_error_generic: "Het lukte niet om de verbinding op te zetten. Probeer het zo opnieuw.",
  attach_label: "Voeg een foto toe",
  attach_aria: "Voeg een foto toe",
  attach_uploading: "Bezig met versturen",
  attach_retry: "Probeer opnieuw",
  attach_remove: "Verwijder",
  attach_error_too_large: "Het bestand is te groot. Probeer een foto onder 10 MB.",
  attach_error_unsupported_type:
    "Dit bestandstype kan ik niet lezen. Probeer een foto in JPG, PNG of WEBP.",
  attach_error_generic: "Het uploaden lukte even niet. Probeer het opnieuw.",
  welcome_card_title: "Welkom bij {brand}.",
  welcome_powered_by: "Powered by RespondHero",
  welcome_chat_card_title: "Chat met {name}",
  welcome_chat_card_intro:
    "Onze AI-collega helpt je 24/7 met je vragen over producten, bestellingen en bezorging.",
  welcome_chat_card_cta: "Start gesprek",
  welcome_direct_contact_heading: "Direct contact",
  welcome_channel_whatsapp_label: "WhatsApp",
  welcome_channel_email_label: "E-mail",
  welcome_channel_phone_label: "Telefoon",
  welcome_channel_unavailable: "Binnenkort beschikbaar",
  welcome_back_aria: "Terug naar welkomstscherm",
  chat_header_agent_tag: "AI agent",
  operator_email_label: "Bericht van het team van {brand}",
  no_source_email_prompt_title: "Wil je dat een collega bij je op terugkomt?",
  no_source_email_prompt_body:
    "Laat je email achter, dan stuurt zij je het antwoord zodra ze er even naar heeft kunnen kijken.",
  no_source_email_input_placeholder: "jouw@email.nl",
  no_source_email_send_label: "Stuur",
  no_source_email_invalid_error:
    "Dat lijkt geen geldig email-adres. Probeer iets als jan@voorbeeld.nl.",
  no_source_email_network_error: "Het lukte even niet. Probeer het zo opnieuw.",
  no_source_email_thanks: "Bedankt, een collega komt er bij je op terug op {email}.",
};

const EN: Strings = {
  launcher_aria: "Open chat",
  panel_title_default: "Need help",
  opener_default: "Hi, ask anything, I'm here to help.",
  composer_placeholder: "Type a message",
  send: "Send",
  sending: "Sending",
  reset: "New conversation",
  ai_disclaimer: "You're talking to our AI assistant. We loop in a teammate when needed.",
  error_generic: "Something went wrong. Please try again.",
  error_offline: "No connection. Please try again later.",
  tool_running: "Looking that up ({name})",
  tool_failed: "{name} ran into a problem",
  source_consulted: "Source consulted",
  composer_input_aria: "Type a message, press Enter to send",
  voice_button_aria: "Talk to Yara",
  voice_modal_title: "Call with Yara",
  voice_idle_body: "Tap the button and ask anything, Yara hears you and answers right away.",
  voice_start_label: "Tap to call",
  voice_connecting: "Connecting",
  voice_listening: "Yara is listening",
  voice_live: "Your call is live",
  voice_end: "End call",
  voice_close_aria: "Close",
  voice_back_to_text: "Back to chat",
  voice_permission_denied: "Your microphone is blocked. Allow it in the browser to talk to Yara.",
  voice_error_generic: "We could not start the connection. Please try again in a moment.",
  attach_label: "Add a photo",
  attach_aria: "Add a photo",
  attach_uploading: "Sending",
  attach_retry: "Try again",
  attach_remove: "Remove",
  attach_error_too_large: "That file is too large. Please try a photo under 10 MB.",
  attach_error_unsupported_type:
    "I cannot read that file type. Please try a JPG, PNG or WEBP photo.",
  attach_error_generic: "The upload did not go through. Please try again.",
  welcome_card_title: "Welcome to {brand}.",
  welcome_powered_by: "Powered by RespondHero",
  welcome_chat_card_title: "Chat with {name}",
  welcome_chat_card_intro:
    "Our AI teammate is here 24/7 to help with products, orders, and delivery.",
  welcome_chat_card_cta: "Start conversation",
  welcome_direct_contact_heading: "Direct contact",
  welcome_channel_whatsapp_label: "WhatsApp",
  welcome_channel_email_label: "Email",
  welcome_channel_phone_label: "Phone",
  welcome_channel_unavailable: "Coming soon",
  welcome_back_aria: "Back to welcome",
  chat_header_agent_tag: "AI agent",
  operator_email_label: "Message from the {brand} team",
  no_source_email_prompt_title: "Want a teammate to follow up?",
  no_source_email_prompt_body:
    "Leave your email and she'll send you the answer as soon as she has had a look.",
  no_source_email_input_placeholder: "you@email.com",
  no_source_email_send_label: "Send",
  no_source_email_invalid_error:
    "That doesn't look like a valid email. Try something like jane@example.com.",
  no_source_email_network_error: "That didn't go through. Please try again in a moment.",
  no_source_email_thanks: "Thanks, a teammate will get back to you at {email}.",
};

const DE: Strings = {
  launcher_aria: "Chat oeffnen",
  panel_title_default: "Hilfe noetig",
  opener_default: "Hallo, frag was du moechtest, ich helfe gerne.",
  composer_placeholder: "Nachricht schreiben",
  send: "Senden",
  sending: "Sendet",
  reset: "Neues Gespraech",
  ai_disclaimer: "Du sprichst mit unserem KI-Assistenten. Bei Bedarf holen wir eine Kollegin dazu.",
  error_generic: "Etwas ist schiefgelaufen. Bitte versuche es gleich noch einmal.",
  error_offline: "Keine Verbindung. Bitte versuche es spaeter noch einmal.",
  tool_running: "Schaue nach ({name})",
  tool_failed: "{name} hat ein Problem",
  source_consulted: "Quelle konsultiert",
  composer_input_aria: "Nachricht schreiben, Enter zum Senden",
  voice_button_aria: "Mit Yara sprechen",
  voice_modal_title: "Mit Yara telefonieren",
  voice_idle_body:
    "Tippe auf den Knopf und stelle deine Frage, Yara hoert dich und antwortet sofort.",
  voice_start_label: "Anruf starten",
  voice_connecting: "Verbinde",
  voice_listening: "Yara hoert zu",
  voice_live: "Dein Gespraech laeuft",
  voice_end: "Gespraech beenden",
  voice_close_aria: "Schliessen",
  voice_back_to_text: "Zurueck zum Chat",
  voice_permission_denied:
    "Dein Mikrofon ist blockiert. Erlaube es im Browser, um mit Yara zu sprechen.",
  voice_error_generic:
    "Die Verbindung konnte nicht aufgebaut werden. Bitte versuche es gleich noch einmal.",
  attach_label: "Foto hinzufuegen",
  attach_aria: "Foto hinzufuegen",
  attach_uploading: "Wird gesendet",
  attach_retry: "Erneut versuchen",
  attach_remove: "Entfernen",
  attach_error_too_large: "Die Datei ist zu gross. Bitte ein Foto unter 10 MB verwenden.",
  attach_error_unsupported_type:
    "Dieses Dateiformat kann ich nicht lesen. Bitte JPG, PNG oder WEBP verwenden.",
  attach_error_generic: "Der Upload hat nicht geklappt. Bitte erneut versuchen.",
  welcome_card_title: "Willkommen bei {brand}.",
  welcome_powered_by: "Powered by RespondHero",
  welcome_chat_card_title: "Chat mit {name}",
  welcome_chat_card_intro:
    "Unsere KI-Kollegin hilft dir rund um die Uhr bei Fragen zu Produkten, Bestellungen und Lieferung.",
  welcome_chat_card_cta: "Gespraech starten",
  welcome_direct_contact_heading: "Direkter Kontakt",
  welcome_channel_whatsapp_label: "WhatsApp",
  welcome_channel_email_label: "E-Mail",
  welcome_channel_phone_label: "Telefon",
  welcome_channel_unavailable: "Bald verfuegbar",
  welcome_back_aria: "Zurueck zum Willkommen",
  chat_header_agent_tag: "KI-Assistentin",
  operator_email_label: "Nachricht vom Team von {brand}",
  no_source_email_prompt_title: "Soll sich eine Kollegin bei dir melden?",
  no_source_email_prompt_body:
    "Lass deine E-Mail da, sie schickt dir die Antwort, sobald sie nachgeschaut hat.",
  no_source_email_input_placeholder: "deine@email.de",
  no_source_email_send_label: "Senden",
  no_source_email_invalid_error:
    "Das sieht nicht nach einer gueltigen E-Mail aus. Versuche etwas wie jan@beispiel.de.",
  no_source_email_network_error: "Das hat gerade nicht geklappt. Bitte gleich noch einmal versuchen.",
  no_source_email_thanks: "Danke, eine Kollegin meldet sich bei dir unter {email}.",
};

const FR: Strings = {
  launcher_aria: "Ouvrir le chat",
  panel_title_default: "Besoin d'aide",
  opener_default: "Bonjour, posez votre question, je suis la pour vous aider.",
  composer_placeholder: "Ecrire un message",
  send: "Envoyer",
  sending: "Envoi",
  reset: "Nouvelle conversation",
  ai_disclaimer: "Vous parlez a notre assistant IA. Nous transmettons a un collegue si besoin.",
  error_generic: "Une erreur est survenue. Veuillez reessayer.",
  error_offline: "Pas de connexion. Veuillez reessayer plus tard.",
  tool_running: "Je verifie ({name})",
  tool_failed: "{name} a rencontre un souci",
  source_consulted: "Source consultee",
  composer_input_aria: "Ecrire un message, appuyez sur Entree pour envoyer",
  voice_button_aria: "Parler a Yara",
  voice_modal_title: "Appel avec Yara",
  voice_idle_body:
    "Appuyez sur le bouton et posez votre question, Yara vous entend et repond immediatement.",
  voice_start_label: "Appuyez pour appeler",
  voice_connecting: "Connexion",
  voice_listening: "Yara ecoute",
  voice_live: "Votre appel est en cours",
  voice_end: "Terminer l'appel",
  voice_close_aria: "Fermer",
  voice_back_to_text: "Retour au chat",
  voice_permission_denied:
    "Votre microphone est bloque. Autorisez-le dans le navigateur pour parler a Yara.",
  voice_error_generic: "Impossible d'etablir la connexion. Veuillez reessayer dans un instant.",
  attach_label: "Ajouter une photo",
  attach_aria: "Ajouter une photo",
  attach_uploading: "Envoi en cours",
  attach_retry: "Reessayer",
  attach_remove: "Retirer",
  attach_error_too_large: "Le fichier est trop grand. Essayez une photo de moins de 10 Mo.",
  attach_error_unsupported_type:
    "Je ne peux pas lire ce type de fichier. Essayez une photo JPG, PNG ou WEBP.",
  attach_error_generic: "L'envoi n'a pas abouti. Veuillez reessayer.",
  welcome_card_title: "Bienvenue chez {brand}.",
  welcome_powered_by: "Propulse par RespondHero",
  welcome_chat_card_title: "Discuter avec {name}",
  welcome_chat_card_intro:
    "Notre collegue IA repond 24h/24 a vos questions sur les produits, commandes et livraisons.",
  welcome_chat_card_cta: "Demarrer la conversation",
  welcome_direct_contact_heading: "Contact direct",
  welcome_channel_whatsapp_label: "WhatsApp",
  welcome_channel_email_label: "E-mail",
  welcome_channel_phone_label: "Telephone",
  welcome_channel_unavailable: "Bientot disponible",
  welcome_back_aria: "Retour a l'accueil",
  chat_header_agent_tag: "Assistant IA",
  operator_email_label: "Message de l'equipe de {brand}",
  no_source_email_prompt_title: "Voulez-vous qu'une collegue vous recontacte?",
  no_source_email_prompt_body:
    "Laissez votre e-mail, elle vous envoie la reponse des qu'elle a pu regarder.",
  no_source_email_input_placeholder: "vous@email.fr",
  no_source_email_send_label: "Envoyer",
  no_source_email_invalid_error:
    "Cela ne ressemble pas a un e-mail valide. Essayez par exemple jean@exemple.fr.",
  no_source_email_network_error: "Cela n'a pas fonctionne. Veuillez reessayer dans un instant.",
  no_source_email_thanks: "Merci, une collegue vous recontactera sur {email}.",
};

const STRINGS: Record<SupportedLocale, Strings> = {
  "nl-NL": NL,
  "nl-BE": NL,
  en: EN,
  de: DE,
  fr: FR,
};

export function resolveLocale(
  explicit: string | null,
  tenantDefault: string | null,
): SupportedLocale {
  const candidates = [explicit, tenantDefault, navigator.language ?? "en"];
  for (const cand of candidates) {
    if (!cand) continue;
    // Match nl-NL, nl-BE, en, etc. We are deliberately permissive on
    // capitalisation to handle `nl-nl` from misconfigured sites.
    const norm = cand.toLowerCase();
    if (norm.startsWith("nl-be")) return "nl-BE";
    if (norm.startsWith("nl")) return "nl-NL";
    if (norm.startsWith("de")) return "de";
    if (norm.startsWith("fr")) return "fr";
    if (norm.startsWith("en")) return "en";
  }
  return "en";
}

export function tFor(locale: SupportedLocale): Strings {
  return STRINGS[locale];
}

export function format(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? `{${k}}`);
}
